import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, User, MapPin, ArrowLeft, Building2 } from 'lucide-react';
import { useTalentProfile } from '@/hooks/useConnectApi';
import { SOURCE_BADGE_CONFIG, type TechnicianTypeSource } from '@/utils/resolveTechnicianTypeTitle';
import { useAuth } from '@/contexts/AuthContext';
import { PassportCertificationsSection } from '@/components/passport/PassportCertificationsSection';
import { PassportExperienceSection } from '@/components/passport/PassportExperienceSection';
import { PassportReferencesSection } from '@/components/passport/PassportReferencesSection';
import { PassportEducationSection } from '@/components/passport/PassportEducationSection';
import { PassportEligibilitySection } from '@/components/passport/PassportEligibilitySection';
import { StudentVerificationSummary } from '@/components/connect/StudentVerificationSummary';
import { ConnectWorkLedgerProjection } from '@/components/connect/ConnectWorkLedgerProjection';
import { filterPassportFields, type PassportViewMode } from '@/lib/passport/fieldVisibility';
import { PassportSection } from '@/components/passport/PassportSection';
import { resolveAudiencesFromViewMode } from '@/lib/passport/sectionAudience';

const ROLE_LABELS: Record<string, string> = {
  technician: 'Technician',
  advisor: 'Advisor',
  manager: 'Manager',
  student: 'Student',
};

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  seeking: { label: 'Actively Seeking', variant: 'default' },
  quietly_open: { label: 'Quietly Open', variant: 'secondary' },
  not_seeking: { label: 'Not Seeking', variant: 'outline' },
};

const TalentProfileView = () => {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, activeShop } = useAuth();

  const isSelf = user?.id === userId;
  const requestedMode = searchParams.get('mode');

  // Determine effective view mode (R73 — A-C-02)
  const viewMode: PassportViewMode = isSelf
    ? 'self'
    : requestedMode === 'in_scope'
      ? 'in_scope'
      : 'connect';

  const shopId = viewMode === 'in_scope' ? null : (isSelf ? null : activeShop?.id ?? null);

  const { data: rawProfile, isLoading, error } = useTalentProfile(shopId, userId ?? '', viewMode);

  // Defense in depth: client-side field filtering on top of server-side
  // nullification. This governs FIELDS OF THE PROFILE ROW and nothing else.
  const profile = rawProfile ? filterPassportFields(rawProfile, viewMode) : null;

  // CORE-C GAP-004. A third, separate mechanism: whether a substantive SECTION
  // may render in this audience at all. Deliberately kept apart from the two
  // above - can_view_passport_data and the RPCs decide what may be RETRIEVED,
  // filterPassportFields decides which profile-row fields survive, and this
  // decides which sections the composition draws. None stands in for another,
  // and this one is composition policy, never authorization.
  const audiences = resolveAudiencesFromViewMode(viewMode);

  // Core-100 R4 W2 (CORE-Q): opening someone else's Passport is recorded by the server inside the read itself
  // (get_talent_profile / get_in_scope_passport), with the server-computed basis — not by a second browser call.

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container mx-auto p-6 max-w-2xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              {error ? 'This profile is not available.' : 'Profile not found.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-2xl space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">{profile.display_name}</CardTitle>
              <CardDescription className="flex items-center gap-2 flex-wrap">
                {viewMode === 'in_scope' && (
                  <Badge variant="secondary" className="gap-1">
                    <Building2 className="h-3 w-3" />
                    In-Scope View
                  </Badge>
                )}
                {profile.open_to_work_status && STATUS_LABELS[profile.open_to_work_status] && (
                  <Badge variant={STATUS_LABELS[profile.open_to_work_status].variant}>
                    {STATUS_LABELS[profile.open_to_work_status].label}
                  </Badge>
                )}
                {profile.distance_miles != null && (
                  <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                    <MapPin className="h-3 w-3" />
                    ~{profile.distance_miles} mi
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Technician Type Title */}
          {profile.technician_type_title && profile.technician_type_title !== 'Technician' && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-muted-foreground">Technician Type</p>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{profile.technician_type_title}</span>
                {profile.technician_type_source && profile.technician_type_source !== 'none' && (() => {
                  const badge = SOURCE_BADGE_CONFIG[profile.technician_type_source as TechnicianTypeSource];
                  return badge?.label ? (
                    <Badge variant="outline" className={`text-xs ${badge.className}`}>
                      {badge.label}
                    </Badge>
                  ) : null;
                })()}
              </div>
            </div>
          )}

          {/* Roles */}
          <div className="space-y-1.5">
            <p className="text-sm font-medium text-muted-foreground">Roles</p>
            <div className="flex gap-1.5 flex-wrap">
              {profile.roles?.length > 0 ? (
                profile.roles.map((role) => (
                  <Badge key={role} variant="outline">
                    {ROLE_LABELS[role] || role}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">No roles specified</span>
              )}
            </div>
          </div>

          {/* Link to full technician passport if available */}
          {profile.technician_profile_id && (
            <div className="pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/technicians/${profile.technician_profile_id}`)}
              >
                View Full Technician Passport
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Passport Sections — read-only for shop viewers. `isOwner` and
          `isReadOnly` stay EDIT affordances; whether the section may be drawn
          at all is the separate question the boundary answers. */}
      <PassportSection id="certifications" audiences={audiences}>
        <PassportCertificationsSection userId={userId!} isOwner={isSelf} isReadOnly={!isSelf} />
      </PassportSection>
      <PassportSection id="experience" audiences={audiences}>
        <PassportExperienceSection userId={userId!} isOwner={isSelf} isReadOnly={!isSelf} />
      </PassportSection>
      <PassportSection id="education" audiences={audiences}>
        <PassportEducationSection userId={userId!} isOwner={isSelf} isReadOnly={!isSelf} />
      </PassportSection>
      <PassportSection id="references" audiences={audiences}>
        <PassportReferencesSection userId={userId!} isOwner={isSelf} isReadOnly={!isSelf} />
      </PassportSection>
      <PassportSection id="eligibility" audiences={audiences}>
        <PassportEligibilitySection userId={userId!} isOwner={isSelf} isReadOnly={!isSelf} />
      </PassportSection>

      {/* Core-100 R5 (CORE-J D): the person-controlled Connect projection of their work ledger (default OFF).
          connect_work_ledger is the authority (opt-in AND the strict Connect audience); this is composition only. */}
      <PassportSection id="connect-work-ledger" audiences={audiences}>
        <ConnectWorkLedgerProjection userId={userId!} isSelf={isSelf} />
      </PassportSection>

      {/* Student verifications. The security-definer RPC behind this is the
          authority; the declaration is composition policy over it. */}
      {profile.roles?.includes('student') && (
        <PassportSection id="student-verification-summary" audiences={audiences}>
          <StudentVerificationSummary studentUserId={userId!} shopId={shopId} />
        </PassportSection>
      )}
    </div>
  );
};

export default TalentProfileView;
